// ─────────────────────────────────────────────────────────────────────────────
// FILE:    src/app/guards/project-access.guard.ts
// VERSION: v2.1.0 — Race Condition Fix: await permissions.ensureLoaded()
// DATE:    2026-05-21
// ZWECK:   v2 Capability-Aware Route Guard. Prüft ob User die nötigen
//          Capabilities für eine Route hat. Redirect auf /insufficient-permission
//          mit Query-Params die der Page erklären was fehlt.
// DEPS:    capability.service.ts, permission.service.ts, page-access.config.ts,
//          capabilities.enum.ts
// EINBINDUNG: in app.routes.ts: canActivate: [calStatusGuard, projectAccessGuard]
//             import { projectAccessGuard } from './guards/project-access.guard'
// GOOD TO KNOW: Pass-through wenn keine Rule matcht (mit Console-Warning).
//               Neue Routes immer in page-access.config.ts eintragen!
//               Public-scope Routes werden direkt durchgelassen.
//               pslot-Extraktion traversiert den gesamten Route-Tree nach oben.
//
// ─────────────────────────────────────────────────────────────────────────────
// ÄNDERUNG v2.1.0 — RACE CONDITION FIX
// ─────────────────────────────────────────────────────────────────────────────
//   PROBLEM (beobachtet 2026-05-20/21):
//     Bei Page-Refresh / Direkteinstieg auf eine geschützte Route (z.B. Bookmark
//     auf /app-management) lief dieser Guard, BEVOR der PermissionSnapshot
//     geladen war. capabilities.hasAllCapabilities() liest synchron
//     getSnapshot() === null → hasCapability gibt false → DENIED, obwohl der
//     User (z.B. Super-Admin orgPerm=7) durchkommen müsste.
//     Über project-select klappte es, weil der Snapshot dann schon warm war.
//     → Verhalten war NICHT berechtigungs-, sondern timing-abhängig. Kein
//       Security-Feature, sondern ein Bug der berechtigte User aussperrt.
//
//   FIX:
//     Dieser Guard ist bereits async — wir nutzen das jetzt wirklich:
//     `await permissions.ensureLoaded()` VOR dem synchronen Capability-Check.
//     ensureLoaded() ist no-op wenn der Snapshot bereits warm ist (Normalfall,
//     kein zusätzlicher API-Call) und wartet sonst auf refresh().
//
//   DIAGNOSE-LOGGING (v2.1.0):
//     Jeder Schritt loggt Walkthrough-Position, erwarteten und Ist-Wert.
//     Suche in der Browser-Console nach "[projectAccessGuard]".
// ─────────────────────────────────────────────────────────────────────────────

import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree, ActivatedRouteSnapshot } from '@angular/router';
import { CapabilityService } from '../services/capability.service';
import { PermissionService } from '../services/permission.service'; // NEU v2.1.0: für ensureLoaded()
import { findPageAccessRule } from '../config/page-access.config';
import { CAPABILITY_META } from '../core/auth/capabilities.enum';

function extractPslot(route: ActivatedRouteSnapshot): string | null {
  let r: ActivatedRouteSnapshot | null = route;
  while (r) {
    const p = r.paramMap.get('pslot');
    if (p) return p;
    for (const c of r.children || []) {
      const cp = c.paramMap.get('pslot');
      if (cp) return cp;
    }
    r = r.parent;
  }
  return null;
}

export const projectAccessGuard: CanActivateFn = async (
  route, state
): Promise<boolean | UrlTree> => {

  const capabilities = inject(CapabilityService);
  const permissions  = inject(PermissionService); // NEU v2.1.0
  const router = inject(Router);

  // ── Walkthrough Schritt 1: Page-Rule finden ────────────────────────────────
  const rule = findPageAccessRule(state.url);

  if (!rule) {
    console.warn(
      '[projectAccessGuard] Step 1: ⚠️ No PAGE_ACCESS_RULE for:', state.url,
      '→ pass-through. Add a rule in page-access.config.ts.'
    );
    return true;
  }

  console.debug(
    `[projectAccessGuard] Step 1: rule found for ${state.url} → ` +
    `scope=${rule.scope}, requires=[${rule.requires.join(', ') || '∅'}], label="${rule.label}"`
  );

  // ── Walkthrough Schritt 2: triviale Durchlässe ─────────────────────────────
  if (rule.scope === 'public') {
    console.debug('[projectAccessGuard] Step 2: scope=public → ALLOW');
    return true;
  }
  if (rule.requires.length === 0) {
    console.debug('[projectAccessGuard] Step 2: requires=∅ (Drehscheibe) → ALLOW');
    return true;
  }

  // ── Walkthrough Schritt 3: pslot für project-scope extrahieren ─────────────
  const pslot = rule.scope === 'project' ? extractPslot(route) : null;
  if (rule.scope === 'project' && !pslot) {
    console.error(
      '[projectAccessGuard] Step 3: ❌ project-scope rule without pslot:', rule.pattern,
      '→ redirect /project-select'
    );
    return router.createUrlTree(['/project-select']);
  }
  console.debug(`[projectAccessGuard] Step 3: pslot=${pslot ?? '(org-scope, none)'}`);

  // ── Walkthrough Schritt 4: RACE-CONDITION FIX (NEU v2.1.0) ─────────────────
  // Snapshot sicherstellen, BEVOR wir synchron prüfen.
  // Erwartet: ein nicht-null Snapshot. Ist-Wert wird unten gegen die Prüfung gehalten.
  console.debug('[projectAccessGuard] Step 4: ensureLoaded() — warte auf Snapshot falls noch kalt…');
  const snap = await permissions.ensureLoaded();
  console.debug(
    '[projectAccessGuard] Step 4: snapshot ready → ' +
    (snap
      ? `orgPerm=${snap.orgPermission}, isSuperAdmin=${snap.isSuperAdmin} ` +
        `(EXPECTED: nicht-null; bei Super-Admin isSuperAdmin=true)`
      : 'IST=null (User nicht eingeloggt?) — Check wird false ergeben')
  );

  // ── Walkthrough Schritt 5: Capability-Check ────────────────────────────────
  const allowed = capabilities.hasAllCapabilities(rule.requires, pslot);
  console.debug(
    `[projectAccessGuard] Step 5: hasAllCapabilities([${rule.requires.join(', ')}], ` +
    `pslot=${pslot ?? '∅'}) → IST=${allowed} (EXPECTED für berechtigten User: true)`
  );

  if (allowed) {
    console.debug('[projectAccessGuard] Step 5: ✅ ALLOW');
    return true;
  }

  // ── Walkthrough Schritt 6: DENIED — Diagnose, welche Capability fehlt ──────
  const firstMissing = rule.requires.find(c => !capabilities.hasCapability(c, pslot));
  const missingMeta = firstMissing ? CAPABILITY_META[firstMissing] : null;

  console.warn(
    `[projectAccessGuard] Step 6: ❌ DENIED: ${state.url} (${rule.label}) ` +
    `missing: ${firstMissing}` +
    (pslot ? `, pslot=${pslot}` : '') +
    ` | TRIGGER: hasCapability('${firstMissing}') === false ` +
    `bei orgPerm=${snap?.orgPermission ?? 'null'}, isSuperAdmin=${snap?.isSuperAdmin ?? 'null'}`
  );

  return router.createUrlTree(['/insufficient-permission'], {
    queryParams: {
      from: state.url,
      pageLabel: rule.label,
      pageDesc: rule.description,
      scope: rule.scope,
      missingCap: firstMissing || '',
      missingLabel: missingMeta?.label || '',
      missingDesc: missingMeta?.description || '',
      typicalRole: missingMeta?.typicalRole || '',
      pslot: pslot || ''
    }
  });
};
